// src/contexts/useLocationContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { nominatim } from "../api/nominatim";

export interface Location {
  city: string;
  country: string;
  countryCode?: string;                 // 👈 add ISO-2 code (e.g., "HU")
  coords: { lat: number; lng: number };
}

interface LocationContextType {
  location: Location;
  setLocation: (location: Location) => void;
  fetchLocationName: (
    lat: number,
    lng: number,
    lang?: string
  ) => Promise<{ city: string; country: string; countryCode?: string }>;
  updateLocation: (lat?: number, lng?: number, forceUpdate?: boolean) => Promise<Location>;
}

const checkGeoPermission = async (): Promise<'granted' | 'prompt' | 'denied'> => {
  try {
    if (!navigator.permissions) return 'prompt';
    const p = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    return p.state as any;
  } catch {
    return 'prompt';
  }
};

// --- add near the top (below interfaces) ---
const locationKey = (loc?: {
  city?: string;
  country?: string;
  countryCode?: string;
  coords?: { lat?: number; lng?: number };
}) => {
  if (!loc) return '∅';
  const r = (n?: number) => (Number.isFinite(n!) ? n!.toFixed(2) : 'x'); // coarse to ignore jitter
  const city = (loc.city || '').toLowerCase();
  const cc = (loc.countryCode || '').toUpperCase();
  const country = (loc.country || '').toLowerCase();
  const lat = r(loc.coords?.lat);
  const lng = r(loc.coords?.lng);
  const countrySig = cc || country;      // prefer ISO if present
  return `${lat},${lng}|${city}|${countrySig}`;
};

const isSameLocation = (a?: Location, b?: Location) =>
  locationKey(a) === locationKey(b);

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [location, setLocation] = useState<Location>({
    city: 'Unknown',
    country: 'Unknown',
    countryCode: undefined,
    coords: { lat: 0, lng: 0 },
  });

  const [isLocationInitialized, setIsLocationInitialized] = useState(false);

  // Get city, country AND ISO countryCode (uppercased) from Nominatim
  const fetchLocationName = async (
  lat: number,
  lng: number,
  lang = (navigator.language || "en").split("-")[0]
): Promise<{ city: string; country: string; countryCode?: string }> => {
  try {
    const res = await nominatim.get("/reverse", {
      params: {
        lat,
        lon: lng,
        format: "json",
        "accept-language": lang,
      },
    });

    const a = res.data.address || {};
    const city = a.city || a.town || a.village || "Unknown";
    const country = a.country || "Unknown";
    const countryCode = a.country_code
      ? String(a.country_code).toUpperCase()
      : undefined;

    return { city, country, countryCode };
  } catch (err) {
    console.error("Location fetch failed:", err);
    return { city: "Unknown", country: "Unknown", countryCode: undefined };
  }
};



  const getBrowserLocation = () =>
  new Promise<GeolocationPosition>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });

const updateLocation = async (
  lat?: number,
  lng?: number,
  forceUpdate = false
): Promise<Location> =>
  new Promise(async (resolve) => {
    const saveAndReturnLocation = (next: Location) => {
      // If nothing changed, resolve current without broadcasting
      if (isSameLocation(location, next)) {
        setIsLocationInitialized(true);
        resolve(location);
        return;
      }

      // Different → persist + broadcast
      setLocation(next);
      sessionStorage.setItem(
  'selectedLocation',
  JSON.stringify({
    city: next.city,
    country: next.country,
    countryCode: next.countryCode,
  })
);
      setIsLocationInitialized(true);
      resolve(next);
    };

    // ✅ If explicit coords are provided, ALWAYS process them
    if (lat !== undefined && lng !== undefined) {
      try {
        const locData = await fetchLocationName(lat, lng);
        saveAndReturnLocation({
          city: locData.city,
          country: locData.country,
          countryCode: locData.countryCode,
          coords: { lat, lng },
        });
      } catch {
        saveAndReturnLocation({
          city: 'Unknown',
          country: 'Unknown',
          countryCode: undefined,
          coords: { lat, lng },
        });
      }
      return;
    }

    // 🧭 No coords → geolocate (unless already initialized)
    if (!forceUpdate && isLocationInitialized) {
      resolve(location);
      return;
    }

    // 🔐 Check permission first (Safari/Firefox fix)
    const permission = await checkGeoPermission();
    if (permission === 'denied') {
      console.warn('Geolocation permission denied');
      resolve(location);
      return;
    }

    // 🌍 Request browser location
    try {
      const pos = await getBrowserLocation();
      const { latitude, longitude } = pos.coords;

      const locData = await fetchLocationName(latitude, longitude);

      saveAndReturnLocation({
        city: locData.city,
        country: locData.country,
        countryCode: locData.countryCode,
        coords: { lat: latitude, lng: longitude },
      });
    } catch (err) {
      console.warn('Geolocation failed:', err);

      saveAndReturnLocation({
        city: 'Unknown',
        country: 'Unknown',
        countryCode: undefined,
        coords: { lat: 0, lng: 0 },
      });
    }
  });


  useEffect(() => {
    const stored = sessionStorage.getItem('selectedLocation');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);

setLocation((prev) => ({
  ...prev,
  city: parsed.city || 'Unknown',
  country: parsed.country || 'Unknown',
  countryCode: parsed.countryCode,
}));
        setIsLocationInitialized(true);
      } catch {
        console.warn('Failed to parse stored location; ignoring.');
      }
    } else {
      console.warn('No stored location found. User must set it explicitly or we’ll geolocate on demand.');
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{ location, setLocation, fetchLocationName, updateLocation }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = (): LocationContextType => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used within a LocationProvider');
  return ctx;
};
